import {
  ACCESS_TOKEN_REFRESHED_EVENT,
  AUTH_ENDPOINTS,
  AUTH_NO_RETRY_ENDPOINTS,
} from "@/constants/auth";
import { AUTH_PATHS } from "@/constants/paths";
import { authCookies } from "@/lib/cookies";
import { getApiUrl } from "@/lib/app-config";
import { captureAuthFailure } from "@/lib/sentry";

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

// Named cross-tab mutex (Web Locks API): the refresh-token cookie is single-use
// and rotated server-side on every call, so two tabs refreshing at once cause
// the loser to submit an already-rotated token and get logged out. Serializing
// via navigator.locks lets a losing tab pick up the winner's new access token
// (shared via the access-token cookie) instead of racing the network call.
const REFRESH_LOCK_NAME = "mof-admin-token-refresh";

function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return fn();
  }
  return navigator.locks.request(REFRESH_LOCK_NAME, fn);
}

export type RequestConfig = {
  headers?: Record<string, string>;
  /** Query string parameters (plain objects, e.g. filter DTOs). */
  params?: object;
  signal?: AbortSignal;
  timeout?: number;
  credentials?: RequestCredentials;
};

export type UploadConfig = RequestConfig & {
  onProgress?: (event: ProgressEvent) => void;
};

export type DeleteRequestConfig = RequestConfig & {
  body?: unknown;
};

export class ClientHttpError extends Error {
  readonly response: { status: number; data: unknown };
  readonly config: { url: string };

  constructor(status: number, data: unknown, url: string) {
    super(`HTTP ${status}`);
    this.name = "ClientHttpError";
    this.response = { status, data };
    this.config = { url };
  }

  get isUnauthorized() {
    return this.response.status === 401;
  }
  get isForbidden() {
    return this.response.status === 403;
  }
  get isNotFound() {
    return this.response.status === 404;
  }
  get isServerError() {
    return this.response.status >= 500;
  }
}

function isNoRetryPath(path: string): boolean {
  return AUTH_NO_RETRY_ENDPOINTS.some((endpoint) => path.includes(endpoint));
}

function buildUrl(path: string, params?: object): string {
  const base = getApiUrl();
  const pathPart = path.startsWith("/") ? path : `/${path}`;
  const url = new URL(`${base}${pathPart}`);
  if (params) {
    Object.entries(params as Record<string, unknown>).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        url.searchParams.set(k, String(v));
      }
    });
  }
  return url.toString();
}

async function parseBody<T>(response: Response): Promise<T> {
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Some endpoints return HTTP 2xx with `{ success: false, message, ... }` — treat as failure. */
function isFailureEnvelope(
  data: unknown,
): data is Record<string, unknown> & { success: false } {
  return (
    data !== null &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    (data as Record<string, unknown>).success === false
  );
}

function clientStatusFromApiBody(
  data: Record<string, unknown>,
  httpStatus: number,
): number {
  if (typeof data.statusCode === "number") return data.statusCode;
  if (typeof data.status === "number") return data.status;
  if (data.error === "InternalServerErrorException") return 500;
  if (httpStatus >= 400) return httpStatus;
  return 502;
}

async function parseBodyOrRejectFailureEnvelope<T>(
  response: Response,
  path: string,
  method?: string,
): Promise<T> {
  const data = await parseBody<unknown>(response).catch(() => undefined);
  if (data !== undefined && isFailureEnvelope(data)) {
    return Promise.reject(
      new ClientHttpError(
        clientStatusFromApiBody(data, response.status),
        data,
        path,
      ),
    ) as Promise<T>;
  }
  // DELETE / 204 often have no JSON body; avoid `undefined` breaking `res.message` in mutations.
  if (data === undefined && (response.status === 204 || method === "DELETE")) {
    return {} as T;
  }
  return data as T;
}

function clearAuthAndRedirect() {
  authCookies.clearAll();
  window.location.href = AUTH_PATHS.LOGIN;
}

/**
 * Returns true only for definitive auth failures — HTTP 401 or 403 from the
 * refresh endpoint. Network errors, timeouts, and server errors (5xx) should
 * NOT force a logout because the session may still be valid; the user can
 * retry once connectivity is restored.
 */
function isDefinitiveAuthFailure(error: unknown): boolean {
  if (error instanceof Error) {
    const match = error.message.match(/Token refresh failed: (\d+)/);
    if (match) {
      const status = parseInt(match[1], 10);
      return status === 401 || status === 403;
    }
    // "Token refresh: no access token" means the server returned 2xx but
    // without a token — treat as an auth failure so we don't loop forever.
    if (error.message.includes("Token refresh: no access token")) return true;
  }
  return false;
}

/** Extract accessToken from Strapi's wrapped auth response `{ success, data: { accessToken } }`. */
export function extractTokensFromWrappedAuthBody(envelope: unknown): {
  accessToken?: string;
} {
  if (!envelope || typeof envelope !== "object") return {};
  const e = envelope as Record<string, unknown>;

  const readAccessToken = (o: Record<string, unknown>): string | undefined =>
    (typeof o.accessToken === "string" && o.accessToken) ||
    (typeof o.access_token === "string" && o.access_token) ||
    (typeof o.token === "string" && o.token) ||
    undefined;

  const data = e.data;
  if (data !== undefined && data !== null) {
    if (typeof data === "object" && !Array.isArray(data)) {
      const accessToken = readAccessToken(data as Record<string, unknown>);
      if (accessToken) return { accessToken };
    }
    if (typeof data === "string" && data.length > 10) {
      return { accessToken: data };
    }
  }

  return { accessToken: readAccessToken(e) };
}

function notifyAccessTokenRefreshed(accessToken: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(ACCESS_TOKEN_REFRESHED_EVENT, {
      detail: { accessToken },
    }),
  );
}

async function refreshAccessToken(staleToken: string | null): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    return await withRefreshLock(async () => {
      // Another tab may have already refreshed (and rotated the single-use
      // refresh cookie) while we were waiting for the lock. If the access
      // token cookie moved on since our request 401'd, reuse it instead of
      // sending the now-stale refresh cookie ourselves.
      const currentToken = authCookies.getAccessToken();
      if (currentToken && currentToken !== staleToken) {
        notifyAccessTokenRefreshed(currentToken);
        refreshQueue.forEach(({ resolve }) => resolve(currentToken));
        refreshQueue = [];
        return currentToken;
      }

      // No request body — Strapi reads the refresh token exclusively from the
      // HttpOnly `refreshToken` cookie, which the browser sends automatically
      // via `credentials: "include"`.
      const refreshResponse = await fetch(buildUrl(AUTH_ENDPOINTS.REFRESH), {
        method: "POST",
        credentials: "include",
      });

      if (!refreshResponse.ok) {
        throw new Error(`Token refresh failed: ${refreshResponse.status}`);
      }

      const text = await refreshResponse.text();
      let envelope: unknown = {};
      try {
        envelope = text ? JSON.parse(text) : {};
      } catch {
        throw new Error("Token refresh: invalid JSON body");
      }
      const { accessToken: newAccessToken } =
        extractTokensFromWrappedAuthBody(envelope);
      if (!newAccessToken) {
        throw new Error("Token refresh: no access token");
      }

      authCookies.setAccessToken(newAccessToken);
      notifyAccessTokenRefreshed(newAccessToken);

      refreshQueue.forEach(({ resolve }) => resolve(newAccessToken));
      refreshQueue = [];

      return newAccessToken;
    });
  } catch (error) {
    if (import.meta.env.NODE_ENV === "development") {
      console.error("Token refresh failed:", error);
    }
    refreshQueue.forEach(({ reject }) => reject(error));
    refreshQueue = [];

    // Only redirect to login for definitive auth failures (401/403 or missing
    // token). Transient network outages or server errors (5xx) must not force
    // the user out — their refresh cookie is still valid and the next API call
    // will retry the refresh automatically.
    if (isDefinitiveAuthFailure(error)) {
      clearAuthAndRedirect();
    }

    throw error;
  } finally {
    isRefreshing = false;
  }
}

async function doFetch(
  method: string,
  path: string,
  body: unknown,
  config: RequestConfig,
  overrideToken?: string,
): Promise<Response> {
  const {
    headers = {},
    params,
    signal,
    timeout = 10_000,
    credentials = "include",
  } = config;

  const token = overrideToken ?? authCookies.getAccessToken();
  const authHeader: Record<string, string> = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  if (signal) {
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  const isFormData = body instanceof FormData;
  const serializedJsonBody = isFormData
    ? undefined
    : body !== undefined
      ? JSON.stringify(body)
      : undefined;
  const requestBody = isFormData ? body : serializedJsonBody;

  try {
    return await fetch(buildUrl(path, params), {
      method,
      credentials,
      headers: {
        ...(serializedJsonBody !== undefined
          ? { "Content-Type": "application/json" }
          : {}),
        ...authHeader,
        ...headers,
      },
      body: requestBody as BodyInit | undefined,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

/** Authenticated GET that retries once after a silent token refresh (for file downloads). */
export async function getAuthenticatedResponse(
  path: string,
  params?: object,
): Promise<Response> {
  const response = await doFetch("GET", path, undefined, { params });
  if (response.status !== 401 || isNoRetryPath(path)) {
    return response;
  }

  const newToken = await refreshAccessToken(authCookies.getAccessToken());
  return doFetch("GET", path, undefined, { params }, newToken);
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  config: RequestConfig = {},
): Promise<T> {
  const response = await doFetch(method, path, body, config);

  if (response.status !== 401 || isNoRetryPath(path)) {
    if (!response.ok) {
      const data = await parseBody<unknown>(response).catch(() => ({}));
      return Promise.reject(new ClientHttpError(response.status, data, path));
    }
    return parseBodyOrRejectFailureEnvelope<T>(response, path, method);
  }

  let newToken: string;
  try {
    newToken = await refreshAccessToken(authCookies.getAccessToken());
  } catch (refreshErr) {
    captureAuthFailure(refreshErr, { path, status: 401 });
    return Promise.reject(new ClientHttpError(401, {}, path));
  }

  const retry = await doFetch(method, path, body, config, newToken);
  if (!retry.ok) {
    const data = await parseBody<unknown>(retry).catch(() => ({}));
    return Promise.reject(new ClientHttpError(retry.status, data, path));
  }
  return parseBodyOrRejectFailureEnvelope<T>(retry, path, method);
}

function uploadWithXHR<T>(
  method: string,
  path: string,
  body: FormData,
  config: UploadConfig = {},
  overrideToken?: string,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const token = overrideToken ?? authCookies.getAccessToken();
    const url = buildUrl(path, config.params);
    const xhr = new XMLHttpRequest();

    xhr.open(method, url);
    xhr.withCredentials = true;

    if (token) {
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    }
    if (config.headers) {
      Object.entries(config.headers).forEach(([k, v]) =>
        xhr.setRequestHeader(k, v),
      );
    }
    if (config.timeout) {
      xhr.timeout = config.timeout;
    }
    if (config.onProgress && xhr.upload) {
      xhr.upload.addEventListener("progress", config.onProgress);
    }
    if (config.signal) {
      config.signal.addEventListener("abort", () => {
        xhr.abort();
      });
    }

    xhr.onload = () => {
      let data: unknown;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        reject(
          new ClientHttpError(
            xhr.status >= 400 ? xhr.status : 502,
            {
              message:
                xhr.responseText?.slice(0, 500) ||
                "Invalid JSON in response from server",
            },
            path,
          ),
        );
        return;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        if (data !== undefined && isFailureEnvelope(data)) {
          reject(
            new ClientHttpError(
              clientStatusFromApiBody(data, xhr.status),
              data,
              path,
            ),
          );
          return;
        }
        resolve(data as T);
      } else {
        reject(new ClientHttpError(xhr.status, data ?? {}, path));
      }
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.ontimeout = () => reject(new Error("Request timeout"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));

    xhr.send(body);
  });
}

async function uploadWithRefresh<T>(
  method: string,
  path: string,
  body: FormData,
  config: UploadConfig = {},
): Promise<T> {
  try {
    return await uploadWithXHR<T>(method, path, body, config);
  } catch (err) {
    if (
      !(err instanceof ClientHttpError) ||
      !err.isUnauthorized ||
      isNoRetryPath(path)
    ) {
      throw err;
    }

    let newToken: string;
    try {
      newToken = await refreshAccessToken(authCookies.getAccessToken());
    } catch {
      throw err;
    }

    return uploadWithXHR<T>(method, path, body, config, newToken);
  }
}

export const http = {
  get: <T = unknown>(url: string, config?: RequestConfig): Promise<T> =>
    request<T>("GET", url, undefined, config),

  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<T> => request<T>("POST", url, data, config),

  put: <T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<T> => request<T>("PUT", url, data, config),

  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: RequestConfig,
  ): Promise<T> => request<T>("PATCH", url, data, config),

  delete: <T = unknown>(
    url: string,
    config?: DeleteRequestConfig,
  ): Promise<T> => {
    const { body, ...rest } = config ?? {};
    return request<T>("DELETE", url, body, rest);
  },

  upload: <T = unknown>(
    method: string,
    url: string,
    body: FormData,
    config?: UploadConfig,
  ): Promise<T> => uploadWithRefresh<T>(method, url, body, config),
};
